import {
  DayOfWeek,
  IFlattenedTimeType,
  MondayToSunday,
  TimeSlotType,
  TimeSpan,
  TimeZoneMapping,
} from 'fitbeat.models';
import { GymInfoForTimeslots } from '../../../entities';
import { Day } from '../../../helpers';
import { GymTimeSlotsManager } from '../../bookings';

interface IOperatingHourEntry {
  title: string;
  specificDayHtml: string;
  onTheHourClassesHtml: string;
  onHalfHourClassesHtml: string;
  withoutInstructorTimesHtml: string;
  closedAllDayMessageHtml: string;
}

interface IWeekdaySummary {
  text: string;
}

export interface IOperatingHours {
  weekdaySummaries: IWeekdaySummary[];
  days: IOperatingHourEntry[];
}

interface IWeekdayRangeSummary {
  fromDay: string;
  toDay: string;
  times: string;
}

export interface IGymTimeslotBuilderOptions {
  publicHolidays: {
    daysAhead: number;
    from: Date;
  };
  titles: {
    onTheHour?: string;
    onHalfHour?: string;
    withoutInstructor?: string;
    closedAllDay?: string;
  },
}

export function buildOperatingHourEntry(
  title: string,
  times: IFlattenedTimeType[],
  options: IGymTimeslotBuilderOptions,
  specificDay?: string,
): IOperatingHourEntry {
  const headerTitles = {
    onTheHour: options.titles.onTheHour || '48 minute classes',
    onHalfHour: options.titles.onHalfHour || '28 minute classes',
    withoutInstructor: options.titles.withoutInstructor || 'Self-powered (no instructors)',
  };

  function buildSpecificDayHtml() {
    if (!specificDay) {
      return '';
    }

    return `<span>${specificDay}</span>`;
  }

  function createListItem(start: string, end: string, shouldStartOnHour = false) {
    const startAdjusted = shouldStartOnHour
      ? TimeSpan.getOnStartOfHour(start, 'nextHour')
      : start;
    const endAdjusted = shouldStartOnHour
      ? TimeSpan.getOnStartOfHour(end, 'sameHour')
      : end;
    const formattedFrom = TimeSpan.formatHHMMAsTime(startAdjusted, 'HH:mm');
    const formattedTo = TimeSpan.formatHHMMAsTime(endAdjusted, 'HH:mm');
    return `<li>${formattedFrom} - ${formattedTo}</li>`;
  }

  // TODO: #2513: use separate field for with/without instructor instead of peak/offpeak
  const classTimesWithInstructor = times.filter((slot) => slot.type === TimeSlotType.Peak);
  const classTimesWithoutInstructor = times.filter((slot) => slot.type === TimeSlotType.OffPeak);

  const hourClassesInner = classTimesWithInstructor
    .map((slot) => createListItem(slot.start, slot.end, true))
    .join('');
  const halfHourClassesInner = classTimesWithInstructor
    .map((slot) => createListItem(slot.start, slot.end))
    .join('');
  const withoutInstructor = classTimesWithoutInstructor
    .map((slot) => createListItem(slot.start, slot.end))
    .join('');

  function buildHtmlSection(section: string, listItems: string): string {
    if (!listItems) {
      return '';
    }
    return `<h2>${section}</h2><ul>${listItems}</ul>`;
  }

  return {
    title,
    specificDayHtml: buildSpecificDayHtml(),
    onTheHourClassesHtml: buildHtmlSection(headerTitles.onTheHour, hourClassesInner),
    onHalfHourClassesHtml: buildHtmlSection(headerTitles.onHalfHour, halfHourClassesInner),
    withoutInstructorTimesHtml: buildHtmlSection(headerTitles.withoutInstructor, withoutInstructor),
    closedAllDayMessageHtml: '',
  };
}

function buildOperatingHourEntryForPublicHoliday(
  day: Date,
  timezone: string,
  times: IFlattenedTimeType[],
  options: IGymTimeslotBuilderOptions,
): IOperatingHourEntry {
  const entry = buildOperatingHourEntry(
    TimeZoneMapping.dateToFormattedLocalTime(day, timezone, 'D MMMM YYYY'),
    times,
    options,
    'Public Holiday',
  );
  if (times.length === 0) {
    entry.closedAllDayMessageHtml = options.titles.closedAllDay || 'Closed all day';
  }
  return entry;
}

export function buildWeekdaySummaryListItem(day: IWeekdayRangeSummary): IWeekdaySummary {
  const weekdays = day.fromDay === day.toDay
    ? day.fromDay
    : `${day.fromDay} to ${day.toDay}`;
  const text = `${weekdays}: ${day.times}`;
  return {
    text,
  };
}

export function getWeekdayFormattedRangeString(times: IFlattenedTimeType[]): string | undefined {
  if (times.length === 0) {
    return undefined;
  }

  const range = TimeSpan.getItemRange(times);
  const start = TimeSpan.formatHHMMAsTime(range.start, 'shorthand_ampm');
  const end = TimeSpan.formatHHMMAsTime(range.end, 'shorthand_ampm');

  return `${start} - ${end}`;
}

function hasTimeSlots(entry: IOperatingHourEntry) {
  return entry.onTheHourClassesHtml !== ''
    || entry.onHalfHourClassesHtml !== ''
    || entry.withoutInstructorTimesHtml !== '';
}

function hasSameTimeSlots(a: IOperatingHourEntry, b: IOperatingHourEntry) {
  return a.onTheHourClassesHtml === b.onTheHourClassesHtml
    && a.onHalfHourClassesHtml === b.onHalfHourClassesHtml
    && a.withoutInstructorTimesHtml === b.withoutInstructorTimesHtml;
}

function mergeSequenceOfDaysWithSameTimes(days: IOperatingHourEntry[]): IOperatingHourEntry[] {
  const result: IOperatingHourEntry[] = [];
  let rangeEntry: IOperatingHourEntry | undefined;
  let rangeStartTitle: string | undefined;
  for (const entry of days) {
    if (rangeEntry && hasSameTimeSlots(entry, rangeEntry)) {
      rangeEntry.title = `${rangeStartTitle} - ${entry.title}`;
    } else {
      result.push(entry);
      rangeEntry = entry;
      rangeStartTitle = entry.title;
    }
  }
  return result;
}

async function getSummaryNormalWeekdayTimes(
  gym: GymInfoForTimeslots,
  gymTimeSlotsManager: GymTimeSlotsManager,
): Promise<IWeekdaySummary[]> {
  const gymDays = await gymTimeSlotsManager.getAllNormalDayGymTimeSlots(gym);
  const normalWeekdayTimes = Day.toDailyTypes(gymDays);

  const days: IWeekdayRangeSummary[] = [];
  let last: IWeekdayRangeSummary | undefined;

  for (const day of MondayToSunday) {
    const times = getWeekdayFormattedRangeString(normalWeekdayTimes[day]);
    const dayOfWeekFormatted = day.substring(0, 3);

    if (!times) {
      // no times for this day, force a new range after this one
      last = undefined;
    } else if (last?.times === times) {
      // extend last item to cover this day
      last.toDay = dayOfWeekFormatted;
    } else {
      last = {
        fromDay: dayOfWeekFormatted,
        toDay: dayOfWeekFormatted,
        times,
      };
      days.push(last);
    }
  }

  return days.map(buildWeekdaySummaryListItem);
}

async function getNormalWeekdayTimes(
  gym: GymInfoForTimeslots,
  gymTimeSlotsManager: GymTimeSlotsManager,
  options: IGymTimeslotBuilderOptions,
) {
  const gymDays = await gymTimeSlotsManager.getAllNormalDayGymTimeSlots(gym);
  const normalWeekdayTimes = Day.toDailyTypes(gymDays);
  const rawGeneralDays = MondayToSunday.map(
    (day: DayOfWeek) => buildOperatingHourEntry(day, normalWeekdayTimes[day], options),
  );
  return mergeSequenceOfDaysWithSameTimes(rawGeneralDays).filter(hasTimeSlots);
}

async function getPublicHolidayTimes(
  gym: GymInfoForTimeslots,
  gymTimeSlotsManager: GymTimeSlotsManager,
  options: IGymTimeslotBuilderOptions,
) {
  const holidays: IOperatingHourEntry[] = [];
  for (let daysAhead = 0; daysAhead < options.publicHolidays.daysAhead; ++daysAhead) {
    const day = TimeSpan.getStartOfDayInTimezonePlusHours(options.publicHolidays.from, gym.timezone, 24 * daysAhead);
    const yyyymmdd = TimeZoneMapping.dateToFormattedLocalTime(day, gym.timezone, 'YYYYMMDD');
    const slotInfo = await gymTimeSlotsManager.getSpecialDayTimeSlot(gym, yyyymmdd);

    if (slotInfo === 'Not a Special Day') {
      // ignore
    } else if (slotInfo === 'Closed All Day') {
      holidays.push(buildOperatingHourEntryForPublicHoliday(day, gym.timezone, [], options));
    } else {
      const flattened = slotInfo.toFlattenedTimeTypes();
      const merged = TimeSpan.merge(
        flattened,
        (a: IFlattenedTimeType, b: IFlattenedTimeType) => a.type === b.type,
      );

      holidays.push(buildOperatingHourEntryForPublicHoliday(day, gym.timezone, merged, options));
    }
  }

  return holidays;
}

export async function getGymOperatingHours(
  gym: GymInfoForTimeslots,
  gymTimeSlotsManager: GymTimeSlotsManager,
  options: IGymTimeslotBuilderOptions,
): Promise<IOperatingHours> {
  const normalWeekDays = await getNormalWeekdayTimes(gym, gymTimeSlotsManager, options);
  const holidays = await getPublicHolidayTimes(
    gym,
    gymTimeSlotsManager,
    options,
  );

  const weekdaySummaries = await getSummaryNormalWeekdayTimes(gym, gymTimeSlotsManager);

  const days = normalWeekDays.concat(holidays);
  return {
    weekdaySummaries,
    days,
  };
}
